import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking
 */
export function initSentry() {
  // Only initialize in production or if explicitly enabled
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE;

  if (!dsn) {
    console.log('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  try {

  Sentry.init({
    dsn,
    environment,

    // Set sample rates
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in production, 100% in dev
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Enable performance monitoring
    integrations: [
      Sentry.browserTracingIntegration({
        // Trace specific routes
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/.*\.amazonaws\.com/,
          /^https:\/\/shockvaluemedia\.com/,
        ],
      }),
      Sentry.replayIntegration({
        // Mask all text and input content
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Don't report errors from these URLs
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors that are expected
      'Network request failed',
      'Failed to fetch',
      'NetworkError',
      // ResizeObserver errors (common, harmless)
      'ResizeObserver loop',
    ],

    // Filter out development/localhost errors in production
    beforeSend(event, hint) {
      // Don't send errors from localhost in production
      if (environment === 'production' && event.request?.url?.includes('localhost')) {
        return null;
      }

      // Add custom context
      if (event.user) {
        // Remove sensitive data
        delete event.user.ip_address;
      }

      return event;
    },

    // Enable debug mode in development
    debug: environment === 'development',
  });
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id: string; email?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
}

/**
 * Clear user context from Sentry
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  try {
    if (context) {
      Sentry.setContext('additional_context', context);
    }
    Sentry.captureException(error);
  } catch (sentryError) {
    console.error('Failed to capture exception with Sentry:', sentryError);
    console.error('Original error:', error);
  }
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Start a transaction for performance monitoring
 * Note: Using newer Sentry API for compatibility
 */
export function startTransaction(name: string, op: string) {
  // Use the newer Sentry API if available
  if (Sentry.startSpan) {
    return Sentry.startSpan({
      name,
      op,
    }, (span) => span);
  }
  
  // Fallback - just return a mock transaction for compatibility
  return {
    setName: (name: string) => {},
    setTag: (key: string, value: string) => {},
    setData: (key: string, value: any) => {},
    finish: () => {},
  };
}
